// Front office (S01–S04): the pipeline lead → research → consultation → proposal → won / lost, from `prospect.list`, with each
// step's gate as the engine enforces it (src/yvc/firm/frontoffice.py `gate`). A prospect's detail — moves, conflicts checks,
// research facts with their sources, the consultation and proposals — is the engine's `prospect.show`, asked through the inbox.
// Moves, conflicts clearance, the consultation and the fee are catalogue commands; the fee and clearance are a partner's acts.
import { useState } from "react";
import { Card, Drawer, PageHeader, StatusPill } from "../../components";
import { fmtDate, fmtStamp } from "../../lib/format";
import { ActButton, Computed, ModelGate, ReadView, useActs, useEngineRead, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
export const STAGES = ["lead", "research", "consultation", "proposal", "won", "lost"] as const;
/** The gate into each stage, as the engine's `gate()` enforces it (firm policy and the standards it cites). */
export const GATES: Record<string, string> = {
  research: "A conflicts check with every hit cleared (ET 1.110.010).",
  consultation: "At least one public-source research fact, cited with its passage; conflicts still clear.",
  proposal: "The consultation recorded, with frameworks, and every nonattest service declined or planned with a designee and safeguards (ET 1.295).",
  won: "An accepted audit proposal.",
  lost: "A reason.",
};
const services = (v: unknown): string[] => { try { const x = typeof v === "string" ? JSON.parse(v) : v; return Array.isArray(x) ? x.map(String) : []; } catch { return []; } };

export function FrontOfficePage() {
  const ms = useFirmModels(["prospects", "acceptance_register"]);
  const [act, drawer] = useActs();
  const [open, setOpen] = useState<Doc | null>(null);
  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <PageHeader title="Front office" subtitle="Pipeline: lead → research → consultation → proposal → won / lost. Each step has a gate the engine enforces."
        actions={<ActButton command="prospect.add" label="Add a lead…" tone="primary" onClick={() => act({ command: "prospect.add", title: "Add a lead" })} />} />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      {ms.data && (
        <ModelGate<Doc[]> kind="prospects" m={ms.data.prospects} label="The pipeline">
          {(rows, m) => (
            <section aria-label="Pipeline" className="space-y-2">
              <div className="flex justify-end"><Computed m={m} /></div>
              <ol className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                {STAGES.map((s) => {
                  const here = rows.filter((p) => p.status === s);
                  return (
                    <li key={s} aria-label={`${s} (${here.length})`} className="flex min-w-0 flex-col gap-2 rounded-md border border-line bg-sunken p-2">
                      <h2 className="flex items-center justify-between text-sm font-semibold capitalize">{s}<span className="rounded bg-raised px-1.5 text-xs tabular-nums text-muted">{here.length}</span></h2>
                      {GATES[s] && <p className="rounded border border-dashed border-line px-2 py-1 text-[11px] text-muted"><span className="font-semibold">Gate:</span> {GATES[s]}</p>}
                      <ul className="space-y-2">
                        {here.length === 0 && <li className="text-xs text-muted">None.</li>}
                        {here.map((p) => (
                          <li key={p.id}>
                            <button type="button" onClick={() => setOpen(p)} className="w-full rounded-md border border-line bg-raised p-2 text-left text-sm hover:border-brand">
                              <span className="block font-medium">{p.organization}</span>
                              <span className="block text-xs text-muted">{services(p.services).join(", ").replace(/_/g, " ") || "no service named"}</span>
                              <span className="block text-[11px] text-muted">since {fmtDate(p.at)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </ModelGate>
      )}
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? String(open.organization) : ""}>
        {open && <ProspectDetail p={open} act={act} />}
      </Drawer>
      {drawer}
    </div>
  );
}

function ProspectDetail({ p, act }: { p: Doc; act: ReturnType<typeof useActs>[0] }) {
  const r = useEngineRead<Doc>("prospect.show", { prospect: p.id }, `prospect ${p.id}`);
  const idx = STAGES.indexOf(p.status);
  const next = idx >= 0 && idx < 4 ? STAGES[idx + 1] : null;
  const closed = p.status === "won" || p.status === "lost";
  return (
    <div className="space-y-4 text-sm">
      <p className="flex flex-wrap items-center gap-2"><StatusPill state={closed ? (p.status === "won" ? "done" : "declined") : "in_progress"} label={p.status} /><span className="text-muted">{p.source}</span></p>
      {!closed && (
        <div role="group" aria-label="Acts on this prospect" className="flex flex-wrap gap-2">
          {p.status === "lead" && <ActButton command="prospect.conflicts" label="Run the conflicts check" onClick={() => act({ command: "prospect.conflicts", fixed: { prospect: p.id }, title: `Conflicts check for ${p.organization}` })} />}
          {p.status === "lead" && <ActButton command="prospect.conflict-clear" label="Clear conflicts…" onClick={() => act({ command: "prospect.conflict-clear", fixed: { prospect: p.id }, title: `Clear the conflicts check for ${p.organization}` })} />}
          {p.status === "research" && <ActButton command="prospect.research" label="Record research" onClick={() => act({ command: "prospect.research", fixed: { prospect: p.id }, title: `Research ${p.organization}` })} />}
          {p.status === "consultation" && <ActButton command="prospect.consult" label="Record the consultation…" onClick={() => act({ command: "prospect.consult", fixed: { prospect: p.id }, title: `Consultation for ${p.organization}` })} />}
          {p.status === "proposal" && <ActButton command="prospect.propose" label="Propose the fee…" tone="primary" onClick={() => act({ command: "prospect.propose", fixed: { prospect: p.id }, title: `Proposal for ${p.organization}` })} />}
          {next && <ActButton command="prospect.move" label={`Move to ${next}…`} tone="primary" onClick={() => act({ command: "prospect.move", fixed: { prospect: p.id, to: next }, title: `Move ${p.organization} to ${next}` })} />}
          <ActButton command="prospect.move" label="Mark lost…" tone="danger" onClick={() => act({ command: "prospect.move", fixed: { prospect: p.id, to: "lost" }, title: `Mark ${p.organization} lost` })} />
        </div>
      )}
      {next && <p className="text-xs text-muted"><span className="font-semibold">Gate to {next}:</span> {GATES[next]} The engine refuses the move, with its reason, until it is met.</p>}
      <ReadView r={r} label="the prospect's record">
        {(d) => (
          <div className="space-y-3">
            <Card title="Pipeline history" bodyClass="p-3">
              {(d.moves ?? []).length === 0 ? <p className="text-muted">No move yet.</p> : (
                <ol className="space-y-1">{(d.moves as Doc[]).map((m, i) => <li key={i}><span className="capitalize">{m.from_status} → {m.to_status}</span> · {m.reason} <span className="text-xs text-muted">({m.by}, {fmtStamp(m.at)})</span></li>)}</ol>
              )}
            </Card>
            <Card title="Conflicts checks" bodyClass="p-3">
              {(d.conflicts ?? []).length === 0 ? <p className="text-muted">No conflicts check yet.</p> : (d.conflicts as Doc[]).map((c) => {
                const hits = services(c.hits);
                return <p key={c.id} className="flex flex-wrap items-center gap-2"><StatusPill state={hits.length && !c.cleared_by ? "blocked" : "done"} label={hits.length ? (c.cleared_by ? "Cleared" : `${hits.length} hit(s)`) : "No hits"} />
                  <span>Names checked: {services(c.names).join(", ")}</span><span className="text-xs text-muted">{c.by}, {fmtStamp(c.at)}</span></p>;
              })}
            </Card>
            <Card title="Research facts" bodyClass="p-3">
              {(d.research ?? []).length === 0 ? <p className="text-muted">No research recorded.</p> : (
                <ul className="space-y-2">{(d.research as Doc[]).map((f) => (
                  <li key={f.id}><span className="font-medium">{f.fact}</span>: {f.value}{f.fiscal_year ? ` (FY ${f.fiscal_year})` : ""}
                    <span className="block text-xs text-muted">{f.source?.startsWith?.("http") ? <a className="text-brand hover:underline" href={f.source} target="_blank" rel="noreferrer">{f.source}</a> : f.source}{f.quote ? ` — “${f.quote}”` : ""}</span></li>
                ))}</ul>
              )}
            </Card>
            <Card title="Consultation and proposals" bodyClass="p-3">
              {d.consultation ? <p>{d.consultation.scope} · frameworks {services(d.consultation.frameworks).join(", ") || String(d.consultation.frameworks ?? "")} · by {d.consultation.by}</p> : <p className="text-muted">No consultation yet.</p>}
              {(d.proposals ?? []).map((pr: Doc) => (
                <div key={pr.id} className="mt-2 flex flex-wrap items-center gap-2 rounded border border-line p-2">
                  <StatusPill state={pr.status} /><span className="flex-1">{String(pr.service).replace(/_/g, " ")} · fee {pr.fee} ({pr.fee_basis})</span>
                  {pr.status === "approved" && <ActButton command="prospect.proposal-sent" label="Mark sent" onClick={() => act({ command: "prospect.proposal-sent", fixed: { proposal: pr.id }, title: `Proposal ${pr.id} sent` })} />}
                  {pr.status === "sent" && <ActButton command="prospect.outcome" label="Record the answer…" onClick={() => act({ command: "prospect.outcome", fixed: { proposal: pr.id }, title: `Answer to proposal ${pr.id}` })} />}
                </div>
              ))}
            </Card>
          </div>
        )}
      </ReadView>
    </div>
  );
}
